import React, { useContext, useEffect } from 'react';
import { CButton, CForm, CModal, CModalBody, CModalFooter, CModalHeader } from '@coreui/react';
import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { connect, DispatchProp } from 'react-redux';
import { isValidPhoneNumber } from 'react-phone-number-input/input';
import TechnicianForm, { TechnicianFormMode } from '../../../technicians/components/TechnicianForm';
import LoadingButton from '../../../shared/components/LoadingButton';
import { UserInfo, UserPayload } from '../../../shared/models/user';
import { RootState } from '../../../store/state';
import { UpdateUserProfileInfo } from '../../actions/profile.action';
import phoneUnification from '../../../shared/utils/validation-utils';
import { ProfileContext } from '../../../shared/contexts/profile.context';

const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  phone: Yup.string().test('phone-validation', 'Please enter a valid US or Canada number', (value) => {
    const phone = phoneUnification(value);
    return phone && isValidPhoneNumber(phone);
  }).required('Phone number is required'),
  email: Yup.string().email('Email must be valid').required('Email is required')
});

export interface EditTechnicianProps extends DispatchProp {
  profile: UserInfo;
  show: boolean;
  close: () => void;
  saving: boolean;
  saved: boolean;
}

const EditProfileModal = (props: EditTechnicianProps) => {
  const { profile, show, close, saving, saved, dispatch } = props;
  const { showEditProfile } = useContext(ProfileContext);

  const onSubmit = (values: UserPayload) => {
    dispatch(new UpdateUserProfileInfo({ userId: profile.id, body: values }));
  };

  const formik = useFormik({
      initialValues: {
        name: profile.name,
        phone: phoneUnification(profile.info.phone),
        email: profile.email,
        roles: profile.roles,
        avatar: profile.avatar,
        username: profile.username
      } as UserPayload,
      validationSchema,
      onSubmit,
    },
  );

  const handleClose = () => {
    close();
    formik.resetForm();
  };

  useEffect(() => {
    if (saved) {
      handleClose();
    }
  }, [saved]);

  return (
    <CModal className="mt-5 pt-5" id="edit-profile-modal" show={ show || showEditProfile } onClose={ handleClose } closeOnBackdrop={ false }>
      <CModalHeader closeButton>Edit Profile</CModalHeader>
      <CForm onSubmit={ formik.handleSubmit }>
        <FormikProvider value={formik}>
          <CModalBody>
            <TechnicianForm formik={ formik } mode={TechnicianFormMode.Profile}/>
          </CModalBody>
          <CModalFooter className="glympse-footer">
            <div className="glympse-btnWrapper">
              <LoadingButton color='primary' className='bg-color-orange border-0 roundedBtn roundBottomShadow pl-5 pr-5' type='submit' isLoading={ saving }>Save Profile</LoadingButton>
            </div>
            <div className="glympse-btnWrapper">
              <CButton className="glympse-cancelBtn" color="light" shape="rounded-pill" variant="ghost" onClick={ handleClose }>Cancel</CButton>
            </div>
          </CModalFooter>
        </FormikProvider>
      </CForm>
    </CModal>
  );
};

const mapStateToProps = (state: RootState) => ({
  saving: state?.profile?.saving,
  saved: state?.profile?.saved,
});

export default connect(mapStateToProps)(EditProfileModal);
